import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { fetchExhibits, type Exhibit as ExhibitType } from "@/lib/api";
import { Exhibit } from "./Exhibit";
import { colors, fonts } from "@/lib/theme";
import { ChevronDown } from "lucide-react";

export function GalleryMain() {
  const [exhibits, setExhibits] = useState<ExhibitType[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const clickTimestamps = useRef<number[]>([]);

  // Triple-tap on "Amy" to access admin (works on mobile and desktop)
  const handleAmyClick = useCallback(() => {
    const now = Date.now();
    // Keep only clicks within the last 1 second
    clickTimestamps.current = clickTimestamps.current.filter(
      (t) => now - t < 1000
    );
    clickTimestamps.current.push(now);

    if (clickTimestamps.current.length >= 3) {
      clickTimestamps.current = [];
      navigate("/admin");
    }
  }, [navigate]);

  useEffect(() => {
    fetchExhibits()
      .then(setExhibits)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: colors.background }}
      >
        <p className="text-xl" style={{ color: colors.textMuted }}>
          Loading gallery...
        </p>
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: colors.background, minHeight: "100vh" }}>
      {/* Hero Landing */}
      <header className="min-h-screen flex flex-col items-center justify-center relative">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: "easeOut" }}
          className="text-center"
        >
          <h1
            className="text-4xl sm:text-5xl md:text-7xl mb-4"
            style={{
              fontFamily: fonts.serif,
              color: colors.text,
              letterSpacing: "-0.02em",
            }}
          >
            <span
              onClick={handleAmyClick}
              className="cursor-default select-none"
            >
              Amy
            </span>{" "}
            Philp Sculpture
          </h1>
          <p
            className="text-lg sm:text-xl md:text-2xl"
            style={{ fontFamily: fonts.sans, color: colors.textMuted }}
          >
            Ceramic & Porcelain Sculpture
          </p>
        </motion.div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5, duration: 1 }}
          className="absolute bottom-12"
        >
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          >
            <ChevronDown size={32} style={{ color: colors.primaryMuted }} />
          </motion.div>
        </motion.div>
      </header>

      {/* Exhibits */}
      <main>
        {exhibits.map((exhibit) => (
          <Exhibit key={exhibit.id} exhibit={exhibit} />
        ))}

        {exhibits.length === 0 && (
          <div className="text-center py-12">
            <p className="text-xl" style={{ fontFamily: fonts.sans, color: colors.textMuted }}>
              No exhibits in the gallery yet.
            </p>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="min-h-screen flex flex-col items-center justify-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8 }}
          className="text-center"
        >
          <p
            className="text-2xl sm:text-3xl md:text-4xl"
            style={{
              fontFamily: fonts.serif,
              color: colors.textMuted,
              letterSpacing: "-0.01em",
            }}
          >
            Thank you for visiting
          </p>
        </motion.div>
      </footer>
    </div>
  );
}
